import { Link } from "react-router-dom";
import { ChevronRight, Sparkles } from "lucide-react";
import Header from "@/components/landing/Header";
import Footer from "@/components/landing/Footer";
import WhatsAppButton from "@/components/landing/WhatsAppButton";
import { Button } from "@/components/ui/button";

/**
 * Reusable layout for SEO pillar / cluster pages.
 * Renders an editorial hero + breadcrumbs + content (children) + FAQ + related links + footer.
 *
 * Props:
 *   meta: { eyebrow, h1, lead }
 *   breadcrumbs: [{ label, href }]
 *   ctaText, ctaHref
 *   faq: [{ q, a }]
 *   relatedLinks: [{ label, href, desc }]
 */
export default function PillarPageLayout({
  meta,
  breadcrumbs = [],
  children,
  ctaText = "Hacer el quiz wellness gratis",
  ctaHref = "/#quiz",
  faq = [],
  relatedLinks = [],
  language = "es",
  t,
  setLanguage,
}) {
  return (
    <main className="relative bg-[#f9f6f0] text-[#1a2421]" data-testid="pillar-page">
      <Header t={t} language={language} setLanguage={setLanguage} />

      {/* ---------- HERO ---------- */}
      <section className="border-b border-[#2c4a3b]/8">
        <div className="max-w-5xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12">
          {/* Breadcrumbs */}
          {breadcrumbs.length > 0 && (
            <nav className="text-xs text-[#5c6b61] mb-6 flex items-center gap-1.5 flex-wrap" aria-label="Breadcrumb">
              {breadcrumbs.map((b, i) => (
                <span key={i} className="flex items-center gap-1.5">
                  {i > 0 && <ChevronRight className="w-3 h-3" />}
                  {b.href ? (
                    <Link to={b.href} className="hover:text-[#bc6c25]">{b.label}</Link>
                  ) : (
                    <span className="text-[#1a2421]">{b.label}</span>
                  )}
                </span>
              ))}
            </nav>
          )}

          {meta.eyebrow && (
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">
              {meta.eyebrow}
            </p>
          )}
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 leading-[1.05] tracking-tight">
            {meta.h1}
          </h1>
          {meta.lead && (
            <p className="mt-6 max-w-2xl text-base md:text-lg text-[#5c6b61] leading-relaxed">
              {meta.lead}
            </p>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild className="rounded-full bg-[#2c4a3b] hover:bg-[#1e3328] text-white px-6 py-6 text-sm font-semibold">
              <a href={ctaHref}>
                <Sparkles className="w-4 h-4 mr-2" /> {ctaText}
              </a>
            </Button>
            <Button asChild variant="outline" className="rounded-full border-[#bc6c25]/40 text-[#9c581e] hover:bg-[#bc6c25]/5 px-6 py-6 text-sm font-semibold">
              <a href="https://www.immunotec.com/es-MX/elioralongevity/products" target="_blank" rel="noopener noreferrer">
                Ver productos Immunotec →
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* ---------- BODY CONTENT (children) ---------- */}
      <article className="max-w-3xl mx-auto px-6 md:px-10 py-16 prose-eliora">
        {children}
      </article>

      {/* ---------- FAQ (with JSON-LD friendly markup) ---------- */}
      {faq.length > 0 && (
        <section className="bg-white border-y border-[#2c4a3b]/8">
          <div className="max-w-3xl mx-auto px-6 md:px-10 py-16">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">Preguntas frecuentes</p>
            <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">Lo que la gente más pregunta</h2>
            <div className="mt-10 space-y-6">
              {faq.map((item, i) => (
                <details key={i} className="group border-b border-[#2c4a3b]/8 pb-5">
                  <summary className="cursor-pointer list-none flex items-start justify-between gap-4">
                    <h3 className="font-serif text-xl text-[#1a2421] group-open:text-[#bc6c25]">{item.q}</h3>
                    <ChevronRight className="w-5 h-5 mt-1 text-[#bc6c25] transition-transform group-open:rotate-90 flex-shrink-0" />
                  </summary>
                  <p className="mt-3 text-[#5c6b61] leading-relaxed">{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ---------- INTERNAL LINKING (cluster) ---------- */}
      {relatedLinks.length > 0 && (
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-6 md:px-10">
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#bc6c25] font-bold">Continúa explorando</p>
            <h2 className="font-serif text-3xl md:text-4xl mt-3 leading-tight">Más recursos del universo Immunotec</h2>
            <div className="mt-8 grid md:grid-cols-3 gap-4">
              {relatedLinks.map((r, i) => (
                <Link
                  key={i}
                  to={r.href}
                  className="group bg-white border border-[#2c4a3b]/8 rounded-2xl p-6 hover:border-[#bc6c25]/40 hover:shadow-lg transition-all"
                >
                  <h3 className="font-serif text-xl text-[#1a2421] group-hover:text-[#bc6c25]">{r.label}</h3>
                  <p className="mt-2 text-sm text-[#5c6b61] leading-relaxed">{r.desc}</p>
                  <p className="mt-4 text-xs font-bold uppercase tracking-[0.18em] text-[#bc6c25] flex items-center gap-1.5">
                    Leer ahora <ChevronRight className="w-3 h-3" />
                  </p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer t={t} />
      <WhatsAppButton label={t?.whatsapp} />
    </main>
  );
}
